import axios from 'axios';
import { API_URL } from '../constants/api';
import {
  CheckIn,
  CheckInResponse,
  TodayCheckInsResponse,
  CheckInHistoryResponse,
  PlayerSearchResult,
  ProcessRfidScanRequest,
  ConfirmCheckInRequest,
  CreateCheckInForUserRequest,
  CreateCheckInForGuestRequest
} from '../types/waiver';

// Field API client with field token
const fieldApiClient = axios.create({
  baseURL: API_URL,
});

fieldApiClient.interceptors.request.use((config) => {
  const fieldToken = localStorage.getItem('fieldToken');
  if (fieldToken) {
    config.headers.Authorization = `Bearer ${fieldToken}`;
  }
  return config;
});

// ==================== Check-In Management API ====================

/**
 * Process RFID scan
 */
export const processRfidScan = async (request: ProcessRfidScanRequest): Promise<CheckInResponse> => {
  const response = await fieldApiClient.post('/api/field/checkin/rfid', request);
  return response.data;
};

/**
 * Get today's check-ins
 */
export const getTodayCheckIns = async (): Promise<TodayCheckInsResponse> => {
  const response = await fieldApiClient.get('/api/field/checkin/today');
  return response.data;
};

/**
 * Get check-ins by date
 */
export const getCheckInsByDate = async (date: string): Promise<CheckIn[]> => {
  const response = await fieldApiClient.get(`/api/field/checkin/date/${date}`);
  return response.data;
};

/**
 * Get check-in history for date range
 */
export const getCheckInHistory = async (startDate: string, endDate: string): Promise<CheckInHistoryResponse> => {
  const response = await fieldApiClient.get('/api/field/checkin/history', {
    params: { startDate, endDate }
  });
  return response.data;
};

/**
 * Create check-in for registered user (manual search)
 */
export const createCheckInForUser = async (request: CreateCheckInForUserRequest): Promise<CheckInResponse> => {
  const response = await fieldApiClient.post('/api/field/checkin/user', request);
  return response.data;
};

/**
 * Create check-in for guest (from signed waiver)
 */
export const createCheckInForGuest = async (request: CreateCheckInForGuestRequest): Promise<CheckInResponse> => {
  const response = await fieldApiClient.post('/api/field/checkin/guest', request);
  return response.data;
};

/**
 * Confirm check-in with game type
 */
export const confirmCheckIn = async (id: string, request: ConfirmCheckInRequest): Promise<void> => {
  await fieldApiClient.post(`/api/field/checkin/${id}/confirm`, request);
};

/**
 * Update game type for check-in
 */
export const updateGameType = async (id: string, gameType: string): Promise<void> => {
  await fieldApiClient.put(`/api/field/checkin/${id}/game-type`, { gameType });
};

/**
 * Search players for check-in
 */
export const searchPlayers = async (searchTerm: string): Promise<PlayerSearchResult[]> => {
  const response = await fieldApiClient.get('/api/field/checkin/search', {
    params: { searchTerm }
  });
  return response.data;
};

/**
 * Get player visit history
 */
export const getPlayerVisitHistory = async (userId: string): Promise<CheckIn[]> => {
  const response = await fieldApiClient.get(`/api/field/checkin/player/${userId}/visits`);
  return response.data;
};

/**
 * Add note to check-in
 */
export const addNote = async (id: string, note: string): Promise<void> => {
  await fieldApiClient.post(`/api/field/checkin/${id}/note`, { note });
};

/**
 * Delete check-in
 */
export const deleteCheckIn = async (id: string): Promise<void> => {
  await fieldApiClient.delete(`/api/field/checkin/${id}`);
};

/**
 * Get game type statistics for date range
 */
export const getGameTypeStats = async (startDate: string, endDate: string): Promise<Record<string, number>> => {
  const response = await fieldApiClient.get('/api/field/checkin/stats', {
    params: { startDate, endDate }
  });
  return response.data;
};

/**
 * Update RFID tag for check-in and user
 */
export const updateRfidTag = async (checkInId: string, userId: string | undefined, rfidTag: string): Promise<void> => {
  await fieldApiClient.put(`/api/field/checkin/${checkInId}/rfid`, { userId, rfidTag });
};
